/* eslint-disable no-magic-numbers */
export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;

const unitToMs: Record<string, number> = {
  w: WEEK,
  d: DAY,
  h: HOUR,
  m: MINUTE,
  s: SECOND,
  ms: 1,
};

const regex = /(\d+(?:\.\d+)?)(w|d|h|m(?!s)|s|ms)/gi;

// Helper type to generate all valid duration unit strings
type DurationUnit = keyof typeof unitToMs;
type DurationToken = `${number}${DurationUnit}`;

/**
 * Accepts: "1h", "2.5d", "10m", "1h30m", etc., or template strings with those tokens
 */
export type DurationString =
  | DurationToken
  | `${DurationToken}${string}` // e.g., "1h30m", "2d4h"
  | TemplateStringsArray;

/**
 * Converts a duration input into milliseconds. if the input is an integer, it is returned as is.
 * @param input - a number (ms), string (e.g., "1h 30m"), or template literal
 * @returns the total duration in milliseconds
 * @throws Error if an unknown time unit is encountered
 * @throws TypeError if the input is not valid
 * @throws TypeError if input is a non-finite or non-integer number
 * @example
 *
 * import { duration } from '@lhb/utils';
 *
 * duration('1h'); // 3600000
 * duration`1h 15m`; // 4500000
 * duration(5000); // 5000
 * const time = 2.5; // in hours
 * duration`${time}h`; // 9000000
 */
function duration(
  input: DurationString | number,
  ...interpolations: unknown[]
): number {
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || !Number.isInteger(input)) {
      throw new TypeError(
        'Only finite integers are allowed as numeric input to duration()',
      );
    }
    return input;
  }

  let parsed: string;

  if (typeof input === 'string') {
    if (!input.trim()) {
      throw new TypeError('Input string cannot be empty');
    }
    parsed = input.trim();
  } else if (Array.isArray(input)) {
    parsed = String.raw({ raw: input }, ...interpolations);
  } else {
    throw new TypeError('Invalid input to duration()');
  }

  const cleaned = parsed.replace(/\s+/g, '');

  let totalMs = 0;
  const matchedUnits = new Set<string>();

  let match: RegExpExecArray | null;

  while ((match = regex.exec(cleaned)) !== null) {
    const value = parseFloat(match[1]);
    const unit = match[2];

    if (!unitToMs[unit]) {
      throw new Error(`Unknown time unit: ${unit}`);
    }

    matchedUnits.add(match[0]);
    totalMs += value * unitToMs[unit];
  }

  if (matchedUnits.size === 0 && cleaned.length > 0) {
    throw new Error(`No valid time units found in input: ${parsed}`);
  }

  return totalMs;
}

/**
 * Checks if the input is a valid duration string or number.
 * @param input - a string or an array of strings representing durations (e.g., "1h", "2m", "3s")
 * @returns true if the input is a valid duration string, false otherwise
 * @example
 * import { duration } from '@lhb/utils';
 * 
 * duration.isDuration('1h'); // true
 * duration.isDuration`2.5h`; // true
 * duration.isDuration(5000); // true
 * duration.isDuration('invalid'); // false
 */
duration.isDuration = (input: unknown): input is DurationString => {
  try {
    duration(input as DurationString);
    return true;
  } catch {
    return false;
  }
};

/**
 * Converts a duration in milliseconds to a human-readable string format.
 * @param input - a number representing milliseconds
 * @returns a string representation of the duration in human-readable format
 * @throws TypeError if the input is not a finite integer
 * @example
 * import { duration } from '@lhb/utils';
 * 
 * duration.toString(60_000); // "1m"
 * duration.toString(3_661_000); // "1h 1m 1s"
 * duration.toString(500); // "500ms"
 */
duration.toString = (input: number): string => {
  if (
    typeof input !== 'number' ||
    !Number.isFinite(input) ||
    !Number.isInteger(input)
  ) {
    throw new TypeError(
      'Input must be a finite integer representing milliseconds',
    );
  }
  const parts: string[] = [];

  for (const [unit, ms] of Object.entries(unitToMs)) {
    if (input >= ms) {
      const value = Math.floor(input / ms);
      parts.push(`${value}${unit}`);
      input -= value * ms;
    }
  }
  if (input > 0) {
    parts.push(`${input}ms`);
  }
  return parts.join(' ');
};

export { duration };

/**
 * Enum representing common durations in milliseconds.
 * Useful for setting timeouts, intervals, or cache expiration.
 * @example
 * import { Duration } from '@lhb/utils';
 *
 * const timeout = setTimeout(() => {
 *  console.log('Timeout reached!');
 * }, Duration.FiveMinutes);
 * clearTimeout(timeout);
 */
export enum Duration {
  OneMinute = duration`1m`,
  TwoMinutes = duration`2m`,
  ThreeMinutes = duration`3m`,
  FourMinutes = duration`4m`,
  FiveMinutes = duration`5m`,
  TenMinutes = duration`10m`,
  FifteenMinutes = duration`15m`,
  TwentyMinutes = duration`20m`,
  ThirtyMinutes = duration`30m`,
  OneHour = duration`1h`,
}
